import React from 'react';
import { Shield, TrendingUp, Clock, AlertTriangle, CheckCircle2, AlertCircle, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';

const CONFIDENCE_COLORS = {
  HIGH: { bg: 'bg-green-600/20', text: 'text-green-400', border: 'border-green-600/30' },
  MODERATE: { bg: 'bg-gold-600/20', text: 'text-gold-400', border: 'border-gold-600/30' },
  MONITOR: { bg: 'bg-red-600/20', text: 'text-red-400', border: 'border-red-600/30' },
  LOW: { bg: 'bg-green-600/20', text: 'text-green-400', border: 'border-green-600/30' },
};

export const RashiiConfidenceMeter = ({ confidenceMeter }) => {
  if (!confidenceMeter) return null;
  
  const { institutional_confidence, liquidity_pressure, deal_urgency } = confidenceMeter;
  
  return (
    <Card className="glass-card border-white/10" data-testid="confidence-meter">
      <CardHeader className="pb-3">
        <CardTitle className="font-display text-lg flex items-center gap-2">
          <Activity className="w-5 h-5 text-primary" />
          Rashii Confidence Meter™
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Institutional Confidence */}
        <div className="p-4 rounded border border-white/10 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Institutional Confidence</span>
            <Badge className={`${CONFIDENCE_COLORS[institutional_confidence?.level]?.bg} ${CONFIDENCE_COLORS[institutional_confidence?.level]?.text} ${CONFIDENCE_COLORS[institutional_confidence?.level]?.border}`}>
              {institutional_confidence?.level === 'HIGH' && <CheckCircle2 className="w-3 h-3 mr-1" />}
              {institutional_confidence?.level === 'MONITOR' && <AlertTriangle className="w-3 h-3 mr-1" />}
              {institutional_confidence?.level}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">{institutional_confidence?.description}</p>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span>Score: {institutional_confidence?.score_basis}</span>
            <span>Risk Factors: {institutional_confidence?.risk_count}</span>
          </div>
        </div>
        
        {/* Liquidity Pressure */}
        <div className="p-4 rounded border border-white/10 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Liquidity Pressure</span>
            <Badge className={`${CONFIDENCE_COLORS[liquidity_pressure?.level]?.bg} ${CONFIDENCE_COLORS[liquidity_pressure?.level]?.text}`}>
              {liquidity_pressure?.level}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">{liquidity_pressure?.description}</p>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span>Avg DOM: {liquidity_pressure?.days_on_market} days</span>
            <span>Liquidity: {liquidity_pressure?.liquidity_score}/100</span>
          </div>
        </div>
        
        {/* Deal Urgency */}
        <div className="p-4 rounded border border-white/10 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Deal Urgency</span>
            <Badge className={`${CONFIDENCE_COLORS[deal_urgency?.level === 'HIGH' ? 'MONITOR' : deal_urgency?.level === 'MODERATE' ? 'MODERATE' : 'HIGH']?.bg} ${CONFIDENCE_COLORS[deal_urgency?.level === 'HIGH' ? 'MONITOR' : deal_urgency?.level === 'MODERATE' ? 'MODERATE' : 'HIGH']?.text}`}>
              <Clock className="w-3 h-3 mr-1" />
              {deal_urgency?.level}
            </Badge>
          </div>
          <div className="space-y-1">
            {deal_urgency?.factors?.map((factor, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm">
                <TrendingUp className="w-3 h-3 text-primary" />
                <span className="text-muted-foreground">{factor}</span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const PersonalizedScoreCard = ({ baseScore, personalizedScore }) => {
  if (!personalizedScore) return null;
  
  const { overall_score, component_scores, applied_weights, weight_adjustments } = personalizedScore;
  
  return (
    <Card className="glass-card border-white/10 gold-glow" data-testid="personalized-score">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="font-display text-lg">Your Personalized Rashii Score</CardTitle>
          <div className="text-right">
            <div className="text-3xl font-display text-primary">{overall_score}</div>
            <div className="text-xs text-muted-foreground">vs {baseScore?.overall_score || 70} base</div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Progress value={overall_score} className="h-3" />
        
        {/* Component Breakdown */}
        <div className="space-y-3">
          {Object.entries(component_scores || {}).map(([key, score]) => {
            const weight = applied_weights?.[key] || 20;
            const adjustment = weight_adjustments?.[key] || 0;
            
            return (
              <div key={key} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="capitalize">{key.replace('_', ' ')}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">{weight}%</span>
                    {adjustment !== 0 && (
                      <span className={adjustment > 0 ? 'text-green-400' : 'text-red-400'}>
                        ({adjustment > 0 ? '+' : ''}{adjustment}%)
                      </span>
                    )}
                    <span className="text-primary font-medium">{score}</span>
                  </div>
                </div>
                <Progress value={score} className="h-1" />
              </div>
            );
          })}
        </div>
        
        <p className="text-xs text-muted-foreground text-center pt-2 border-t border-border">
          Weights dynamically adjusted based on your investment profile
        </p>
      </CardContent>
    </Card>
  );
};

export const AIAnalysisCard = ({ analysis }) => {
  if (!analysis) return null;
  
  const verdictColors = {
    'Strong Fit': 'bg-green-600/20 text-green-400 border-green-600/30',
    'Conditional Fit': 'bg-gold-600/20 text-gold-400 border-gold-600/30',
    'Monitor': 'bg-red-600/20 text-red-400 border-red-600/30'
  };
  
  return (
    <Card className="glass-card border-white/10" data-testid="ai-analysis">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="font-display text-lg flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Why This Works For You
          </CardTitle>
          <Badge className={verdictColors[analysis.rashii_verdict] || verdictColors['Monitor']}>
            {analysis.rashii_verdict}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Main Summary */}
        <p className="text-sm leading-relaxed">{analysis.why_this_works}</p>
        
        {/* Analysis Sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="p-3 rounded bg-secondary/30 space-y-1">
            <div className="text-xs uppercase tracking-widest text-primary">Financial Alignment</div>
            <p className="text-muted-foreground">{analysis.financial_alignment}</p>
          </div>
          
          <div className="p-3 rounded bg-secondary/30 space-y-1">
            <div className="text-xs uppercase tracking-widest text-primary">Strategic Timing</div>
            <p className="text-muted-foreground">{analysis.strategic_timing}</p>
          </div>
          
          <div className="p-3 rounded bg-secondary/30 space-y-1">
            <div className="text-xs uppercase tracking-widest text-primary">Liquidity Outlook</div>
            <p className="text-muted-foreground">{analysis.liquidity_outlook}</p>
          </div>
          
          <div className="p-3 rounded bg-secondary/30 space-y-1">
            <div className="text-xs uppercase tracking-widest text-primary">Exit Logic</div>
            <p className="text-muted-foreground">{analysis.exit_logic}</p>
          </div>
        </div>
        
        {/* Risk Consideration */}
        <div className="p-3 rounded border border-gold-600/30 bg-gold-600/10">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-gold-400 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-xs uppercase tracking-widest text-gold-400">Risk Consideration</div>
              <p className="text-sm text-muted-foreground mt-1">{analysis.risk_consideration}</p>
            </div>
          </div>
        </div>
        
        {/* Verdict */}
        <div className="text-center pt-3 border-t border-border">
          <p className="text-xs text-muted-foreground">{analysis.verdict_reason}</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default { RashiiConfidenceMeter, PersonalizedScoreCard, AIAnalysisCard };
